import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import {
  Box,
  Grid,
  Link as MuiLink,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import pressData from "../data/press.json";
import wapoImage from "../images/wapo-dpi.jpeg";

type PressItem = {
  headline: string;
  summary: string;
  url: string;
  outlet_url: string;
  publication_date: string | null;
  publication_year: string;
  publication: string;
  brand: string;
  category: "Coverage" | "Citation" | "Support";
  hero: string;
  image_key: string;
  is_spotlight: boolean;
};

const pressImages: Record<string, string> = {
  wapo: wapoImage,
};

const pressList = pressData as PressItem[];

const getSortDate = (item: PressItem) =>
  item.publication_date || `${item.publication_year}-01-01`;

const formatDate = (date: string | null, year: string) => {
  if (!date) {
    return year;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(`${date}T00:00:00`));
};

const getItemImage = (item: PressItem) =>
  item.hero || pressImages[item.image_key] || "";

const PressVisual = ({
  item,
  large = false,
}: {
  item: PressItem;
  large?: boolean;
}) => {
  const theme = useTheme();
  const image = getItemImage(item);
  const fallback =
    item.brand === "FT"
      ? {
          accent: "#990f3d",
          backgroundColor: "#fff1e5",
          backgroundImage:
            "linear-gradient(rgba(51, 48, 46, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(51, 48, 46, 0.08) 1px, transparent 1px)",
          color: "#33302e",
        }
      : {
          accent: theme.customColors.supplementary.bluish,
          backgroundColor: theme.customColors.primary.prusianBlue,
          backgroundImage:
            "linear-gradient(rgba(222, 243, 255, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(222, 243, 255, 0.08) 1px, transparent 1px)",
          color: theme.customColors.primary.aliceBlue,
        };

  if (image) {
    return (
      <Box
        sx={{
          backgroundColor: theme.customColors.primary.prusianBlue,
          backgroundImage: `url("${image}")`,
          backgroundPosition: "center",
          backgroundSize: "cover",
          borderRadius: "8px",
          height: large
            ? { lg: "420px", md: "340px", xs: "240px" }
            : { md: "160px", xs: "180px" },
          overflow: "hidden",
          position: "relative",
          width: "100%",
          "&::after": {
            backgroundColor: large
              ? "rgba(8, 31, 48, 0.28)"
              : "rgba(8, 31, 48, 0.08)",
            content: '""',
            inset: 0,
            position: "absolute",
          },
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        alignItems: "stretch",
        backgroundColor: fallback.backgroundColor,
        backgroundImage: fallback.backgroundImage,
        backgroundSize: "24px 24px",
        borderRadius: "8px",
        color: fallback.color,
        display: "flex",
        flexDirection: "column",
        height: large
          ? { lg: "420px", md: "340px", xs: "240px" }
          : { md: "160px", xs: "180px" },
        justifyContent: "space-between",
        overflow: "hidden",
        padding: large ? { lg: "32px", xs: "24px" } : "18px",
        position: "relative",
        width: "100%",
      }}
    >
      <Typography
        sx={{
          color: fallback.accent,
          fontSize: "12px",
          fontWeight: 800,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
        }}
      >
        {item.category}
      </Typography>
      <Typography
        sx={{
          color: fallback.color,
          fontSize: large
            ? { lg: "88px", xs: "56px" }
            : { md: "42px", xs: "46px" },
          fontWeight: 850,
          letterSpacing: "0",
          lineHeight: 0.9,
          maxWidth: "80%",
        }}
      >
        {item.brand}
      </Typography>
    </Box>
  );
};

const Press = () => {
  const theme = useTheme();
  const sortedPress = [...pressList].sort((a, b) =>
    getSortDate(b).localeCompare(getSortDate(a)),
  );
  const spotlightPress =
    sortedPress.find((item) => item.is_spotlight) || sortedPress[0];
  const secondaryFeatured = sortedPress
    .filter((item) => item.headline !== spotlightPress.headline)
    .slice(0, 2);
  const groupedYears = Array.from(
    new Set(sortedPress.map((item) => item.publication_year)),
  );

  return (
    <Box
      sx={{
        width: "100%",
        overflow: "auto",
        padding: { lg: "80px 0 125px", sm: "80px 0 100px", xs: "50px 0 80px" },
      }}
    >
      <Grid container spacing={{ lg: 5, xs: 3 }}>
        <Grid item lg={9} xs={12}>
          <Typography
            sx={{
              fontSize: { lg: "96px", md: "80px", sm: "60px", xs: "48px" },
              lineHeight: "100%",
              fontWeight: 650,
              color: theme.customColors.primary.prusianBlue,
              marginBottom: { lg: "24px", xs: "18px" },
            }}
            variant="h1"
          >
            Press
          </Typography>
          <Typography
            sx={{
              color: theme.customColors.primary.prusianBlue,
              fontSize: { lg: "28px", md: "24px", xs: "19px" },
              fontWeight: 500,
              lineHeight: 1.35,
              maxWidth: "860px",
            }}
          >
            Coverage, citations, and support for our work on AI data provenance,
            web consent, open model ecosystems, and transparency infrastructure.
          </Typography>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={3}
        sx={{ alignItems: "stretch", marginTop: { lg: "60px", xs: "38px" } }}
      >
        <Grid item lg={7} xs={12}>
          <MuiLink
            aria-label={`Open ${spotlightPress.headline}`}
            href={spotlightPress.url}
            target="_blank"
            rel="noreferrer"
            underline="none"
            sx={{ color: "inherit", display: "block" }}
          >
            <PressVisual item={spotlightPress} large />
          </MuiLink>
        </Grid>
        <Grid item lg={5} xs={12}>
          <Box
            sx={{
              borderBottom: `1px solid ${theme.customColors.primary.prusianBlue}`,
              borderTop: `1px solid ${theme.customColors.primary.prusianBlue}`,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              justifyContent: "center",
              padding: { lg: "34px 0", xs: "24px 0" },
            }}
          >
            <Typography
              sx={{
                color: theme.customColors.primary.lion,
                fontSize: "12px",
                fontWeight: 800,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                marginBottom: "16px",
              }}
            >
              Featured coverage
            </Typography>
            <MuiLink
              href={spotlightPress.url}
              target="_blank"
              rel="noreferrer"
              underline="none"
              sx={{ color: "inherit" }}
            >
              <Typography
                variant="h2"
                sx={{
                  color: theme.customColors.primary.prusianBlue,
                  fontSize: { lg: "44px", md: "36px", xs: "29px" },
                  fontWeight: 800,
                  lineHeight: 1.05,
                  marginBottom: "18px",
                  ":hover": { color: theme.customColors.primary.lion },
                }}
              >
                {spotlightPress.headline}
              </Typography>
            </MuiLink>
            <Typography
              sx={{
                color: theme.customColors.primary.prusianBlue,
                fontSize: { lg: "20px", xs: "17px" },
                lineHeight: 1.45,
                marginBottom: "24px",
              }}
            >
              {spotlightPress.summary}
            </Typography>
            <Stack direction={{ sm: "row", xs: "column" }} spacing={1.25}>
              <MuiLink
                href={spotlightPress.url}
                target="_blank"
                rel="noreferrer"
                underline="none"
                sx={{
                  alignItems: "center",
                  color: theme.customColors.primary.lion,
                  display: "flex",
                  fontSize: "16px",
                  fontWeight: 800,
                  gap: "7px",
                }}
              >
                Read article <ArrowOutwardIcon sx={{ fontSize: "18px" }} />
              </MuiLink>
              <MuiLink
                href={spotlightPress.outlet_url}
                target="_blank"
                rel="noreferrer"
                underline="none"
                sx={{
                  color: theme.customColors.supplementary.horizon,
                  fontSize: "16px",
                  fontWeight: 700,
                }}
              >
                {spotlightPress.publication}
              </MuiLink>
            </Stack>
          </Box>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={3}
        sx={{ marginTop: { lg: "30px", xs: "24px" } }}
      >
        {secondaryFeatured.map((item) => (
          <Grid item md={6} xs={12} key={item.headline}>
            <Box
              sx={{
                border: `1px solid ${theme.customColors.primary.prusianBlue}`,
                borderRadius: "8px",
                display: "grid",
                gridTemplateColumns: { sm: "180px 1fr", xs: "1fr" },
                gap: "18px",
                height: "100%",
                padding: "16px",
              }}
            >
              <MuiLink
                aria-label={`Open ${item.headline}`}
                href={item.url}
                target="_blank"
                rel="noreferrer"
                underline="none"
              >
                <PressVisual item={item} />
              </MuiLink>
              <Box>
                <Typography
                  sx={{
                    color: theme.customColors.supplementary.horizon,
                    fontSize: "13px",
                    fontWeight: 700,
                    marginBottom: "8px",
                  }}
                >
                  {formatDate(item.publication_date, item.publication_year)} |{" "}
                  {item.publication}
                </Typography>
                <MuiLink
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  underline="none"
                >
                  <Typography
                    sx={{
                      color: theme.customColors.primary.prusianBlue,
                      fontSize: { md: "24px", xs: "21px" },
                      fontWeight: 800,
                      lineHeight: 1.15,
                      ":hover": { color: theme.customColors.primary.lion },
                    }}
                  >
                    {item.headline}
                  </Typography>
                </MuiLink>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ marginTop: { lg: "72px", xs: "48px" } }}>
        {groupedYears.map((year) => (
          <Box key={year} sx={{ marginBottom: { lg: "58px", xs: "42px" } }}>
            <Typography
              sx={{
                color: theme.customColors.primary.prusianBlue,
                fontSize: { lg: "42px", xs: "32px" },
                fontWeight: 800,
                lineHeight: 1,
                marginBottom: "14px",
              }}
            >
              {year}
            </Typography>
            <Box
              sx={{
                borderTop: `1px solid ${theme.customColors.primary.prusianBlue}`,
              }}
            >
              {sortedPress
                .filter((item) => item.publication_year === year)
                .map((item) => (
                  <Box
                    key={`${item.headline}-${item.publication}`}
                    sx={{
                      borderBottom: `1px solid ${theme.customColors.primary.prusianBlue}`,
                      display: "grid",
                      gridTemplateColumns: {
                        lg: "260px 1fr 190px",
                        md: "220px 1fr",
                        xs: "1fr",
                      },
                      gap: { lg: "28px", md: "22px", xs: "16px" },
                      padding: { lg: "24px 0", xs: "20px 0" },
                    }}
                  >
                    <MuiLink
                      aria-label={`Open ${item.headline}`}
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      underline="none"
                    >
                      <PressVisual item={item} />
                    </MuiLink>
                    <Box>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                          flexWrap: "wrap",
                          gap: "8px",
                          marginBottom: "12px",
                        }}
                      >
                        <Typography
                          sx={{
                            border: `1px solid ${theme.customColors.primary.prusianBlue}`,
                            borderRadius: "999px",
                            color: theme.customColors.primary.prusianBlue,
                            fontSize: "12px",
                            fontWeight: 800,
                            lineHeight: 1,
                            padding: "6px 9px",
                            textTransform: "uppercase",
                          }}
                        >
                          {item.category}
                        </Typography>
                        <Typography
                          sx={{
                            color: theme.customColors.supplementary.horizon,
                            fontSize: "14px",
                            fontWeight: 700,
                          }}
                        >
                          {formatDate(
                            item.publication_date,
                            item.publication_year,
                          )}
                        </Typography>
                      </Stack>
                      <MuiLink
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        underline="none"
                      >
                        <Typography
                          sx={{
                            color: theme.customColors.primary.prusianBlue,
                            fontSize: { lg: "30px", md: "26px", xs: "22px" },
                            fontWeight: 800,
                            lineHeight: 1.12,
                            marginBottom: "12px",
                            ":hover": {
                              color: theme.customColors.primary.lion,
                            },
                          }}
                        >
                          {item.headline}
                        </Typography>
                      </MuiLink>
                      <Typography
                        sx={{
                          color: theme.customColors.primary.prusianBlue,
                          fontSize: { lg: "18px", xs: "16px" },
                          lineHeight: 1.45,
                          maxWidth: "820px",
                        }}
                      >
                        {item.summary}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        alignSelf: "start",
                        display: { lg: "block", md: "none", xs: "block" },
                      }}
                    >
                      <MuiLink
                        href={item.outlet_url}
                        target="_blank"
                        rel="noreferrer"
                        underline="none"
                        sx={{
                          color: theme.customColors.primary.prusianBlue,
                          display: "block",
                          fontSize: "18px",
                          fontWeight: 800,
                          lineHeight: 1.2,
                          marginBottom: "14px",
                          ":hover": { color: theme.customColors.primary.lion },
                        }}
                      >
                        {item.publication}
                      </MuiLink>
                      <MuiLink
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        underline="none"
                        sx={{
                          alignItems: "center",
                          color: theme.customColors.primary.lion,
                          display: "flex",
                          fontSize: "15px",
                          fontWeight: 800,
                          gap: "6px",
                        }}
                      >
                        Read article{" "}
                        <ArrowOutwardIcon sx={{ fontSize: "17px" }} />
                      </MuiLink>
                    </Box>
                  </Box>
                ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Press;
